import { useCallback, useEffect, useState } from "react";
import { Download, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export const LeadsPanel = ({ pw, videoSet }) => {
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { "X-Stats-Key": pw, "Content-Type": "application/json" };

  const load = useCallback(async () => {
    const r = await fetch(`${API}/api/leads/summary`, { headers });
    setSum(await r.json());
  }, [pw]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    setBusy(true);
    const r = await fetch(`${API}/api/leads/send-tour`, { method: "POST", headers, body: JSON.stringify({ origin: window.location.origin }) });
    const b = await r.json().catch(() => ({}));
    setBusy(false);
    if (b.skipped) toast.error("Add the tour video in Settings first.");
    else toast.success(`Tour emails: ${b.sent} sent, ${b.failed} failed`);
    load();
  };

  if (!sum) return null;
  return (
    <section className="mt-12 rounded-[2rem] border border-[#E8DDD3] bg-white p-6 sm:p-8" data-testid="stats-leads">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-xl grid place-items-center bg-[#1F1A17] text-[#FBF8F4]"><Mail className="h-4 w-4" /></span>
          <div>
            <h2 className="font-serif text-2xl tracking-tight text-[#1F1A17]">Tour leads</h2>
            <p className="text-xs text-[#685F58]">People who asked for the 60-second tour. The email (tour + checklist + signup link) sends automatically once a video is set in Settings, and instantly to every new lead after that.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${API}/api/stats/leads.csv?key=${encodeURIComponent(pw)}`} data-testid="leads-export" className="inline-flex items-center gap-2 rounded-full border border-[#E8DDD3] h-10 px-4 text-sm text-[#1F1A17] hover:border-[#1F1A17] transition-colors"><Download className="h-4 w-4" /> Export leads</a>
          <button onClick={send} disabled={busy || sum.waiting === 0} data-testid="leads-send-tour" className="inline-flex items-center gap-2 rounded-full bg-[#1F1A17] hover:bg-[#B4532A] disabled:opacity-60 h-10 px-4 text-sm text-[#FBF8F4] transition-colors"><Send className="h-4 w-4" /> {busy ? "Sending…" : `Send tour to ${sum.waiting} waiting`}</button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[["Leads", sum.total, "leads-total"], ["Tour sent", sum.sent, "leads-sent"], ["Waiting", sum.waiting, "leads-waiting"], ["Failed", sum.failed, "leads-failed"]].map(([l, v, id]) => (
          <div key={l} className="rounded-2xl border border-[#E8DDD3] p-5" data-testid={id}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#685F58]">{l}</div>
            <div className="mt-2 font-serif text-3xl text-[#1F1A17]">{v}</div>
          </div>
        ))}
      </div>
      {!videoSet && sum.waiting > 0 && (
        <p className="mt-4 rounded-xl bg-[#FDEEE8] px-4 py-2.5 text-xs text-[#9C421E]" data-testid="leads-video-hint">
          {sum.waiting} {sum.waiting === 1 ? "person is" : "people are"} waiting. Paste the tour video link in Settings below and press Save — the emails go out automatically.
        </p>
      )}
    </section>
  );
};
